<template>
    <HomeSectionContainer :title="t('settings.account.title')">
        <template #subtitle>
            <p :id="settingsNoticeId" class="body-small text-ink-muted">{{ t("settings.account.saveNotice") }}</p>
        </template>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
            <AccountSetting :error="errors[FIELDS.LANGUAGE]">
                <template #label>
                    <span class="label-with-info">
                        {{ t("settings.account.language.label") }}
                        <BaseTooltip
                            open-on-click
                            side="top"
                            :side-offset="8"
                            content-class="max-w-80 p-3 text-left shadow-lg"
                            :trigger-aria-label="t('settings.account.language.tooltipTrigger')">
                            <template #trigger>
                                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('settings.account.language.tooltipTrigger')">
                                    <i class="bi bi-info-circle" aria-hidden="true"></i>
                                </BaseButton>
                            </template>
                            <template #content>
                                <p class="body-small">{{ t("settings.account.language.tooltip") }}</p>
                            </template>
                        </BaseTooltip>
                    </span>
                </template>
                <template #content>
                    <AccountSettingSelect
                        :id="idForField(FIELDS.LANGUAGE)"
                        :aria-label="t('settings.account.language.label')"
                        :described-by="settingsNoticeId"
                        :choices="languageChoices"
                        :model-value="preferenceValues[FIELDS.LANGUAGE] || ''"
                        @update:model-value="(value) => setPreference(FIELDS.LANGUAGE, value)" />
                </template>
            </AccountSetting>
            <AccountSetting :error="errors.choice">
                <template #label>{{ t("settings.account.cookies.label") }}</template>
                <template #content>
                    <AccountSettingSelect
                        :id="idForField(FIELDS.COOKIE_CONSENT_CHOICE)"
                        :aria-label="t('settings.account.cookies.label')"
                        :described-by="settingsNoticeId"
                        :choices="cookieConsentChoices"
                        :disabled="cookiePreferenceSaving"
                        :model-value="preferenceValues[FIELDS.COOKIE_CONSENT_CHOICE] || ''"
                        @update:model-value="(value) => setPreference(FIELDS.COOKIE_CONSENT_CHOICE, value)" />
                    <p v-if="cookiePreferenceError" role="alert" class="body-small text-button-danger" data-testid="cookie-preference-error">
                        Couldn't save that just now. Try again.
                    </p>
                </template>
            </AccountSetting>
            <AccountSetting v-if="canReceiveEmails" :error="errors[FIELDS.CHECK_INS]">
                <template #label>{{ t("settings.account.checkIns.label") }}</template>
                <template #content>
                    <AccountSettingToggle
                        :id="idForField(FIELDS.CHECK_INS)"
                        :label="t('settings.account.checkIns.toggle')"
                        :model-value="preferenceValues[FIELDS.CHECK_INS]"
                        @update:model-value="(value) => setPreference(FIELDS.CHECK_INS, value)"></AccountSettingToggle>
                </template>
            </AccountSetting>
            <AccountSetting v-if="micConsent.policy_enabled">
                <template #label>{{ t("settings.account.microphone.label") }}</template>
                <template #content>
                    <AccountSettingToggle
                        id="mic-consent-toggle"
                        :label="t('settings.account.microphone.toggle')"
                        :model-value="micConsentGranted"
                        @update:model-value="setMicConsent"></AccountSettingToggle>
                    <p v-if="micConsentError" role="alert" class="body-small text-button-danger" data-testid="mic-consent-error">
                        {{ t("settings.account.microphone.saveError") }}
                    </p>
                </template>
            </AccountSetting>
            <AccountSetting :error="errors[FIELDS.IS_MANAGER]">
                <template #label>{{ t("settings.account.manager.label") }}</template>
                <template #content>
                    <AccountSettingToggle
                        :id="idForField(FIELDS.IS_MANAGER)"
                        :label="t('settings.account.manager.toggle')"
                        :model-value="preferenceValues[FIELDS.IS_MANAGER]"
                        @update:model-value="(value) => setPreference(FIELDS.IS_MANAGER, value)"></AccountSettingToggle>
                </template>
            </AccountSetting>
        </div>
        <AccountSetting v-if="customInstructionsEnabled" class="mt-3" :error="errors[FIELDS.CUSTOM_INSTRUCTIONS]">
            <template #label>{{ t("settings.account.customInstructions.label") }}</template>
            <template #content>
                <AccountSettingTextarea
                    :id="idForField(FIELDS.CUSTOM_INSTRUCTIONS)"
                    :aria-label="t('settings.account.customInstructions.label')"
                    :maxlength="1500"
                    :placeholder="t('settings.account.customInstructions.placeholder')"
                    :model-value="preferenceValues[FIELDS.CUSTOM_INSTRUCTIONS]"
                    @save="(value) => setPreference(FIELDS.CUSTOM_INSTRUCTIONS, value)" />
            </template>
        </AccountSetting>
    </HomeSectionContainer>
</template>

<script>
export const ERROR_BAG = "account_settings";
</script>

<script setup>
import { getCookie } from "/js/utils.js";
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import AccountSetting from "~vue/UserProfile/AccountSetting.vue";
import AccountSettingSelect from "~vue/UserProfile/AccountSettingSelect.vue";
import AccountSettingTextarea from "~vue/UserProfile/AccountSettingTextarea.vue";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { announceCookieConsentChange, rememberBrowserCookieChoice } from "~vue/utils/cookieConsent";
import { inject, ref, toRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const settingsNoticeId = useId();

const FIELDS = {
    LANGUAGE: "default_language",
    CHECK_INS: "prefers_weekly_check_in",
    IS_MANAGER: "is_manager",
    COOKIE_CONSENT_CHOICE: "cookie_consent_choice",
    CUSTOM_INSTRUCTIONS: "custom_instructions",
};

const props = defineProps({
    userProfile: { type: Object, required: true },
    languageChoices: { type: Array, required: true },
    cookieConsentChoices: { type: Array, required: true },
    errors: { type: Object, default: () => ({}) },
    endpoints: { type: Object, required: true },
    canReceiveEmails: { type: Boolean, default: true },
    customInstructionsEnabled: { type: Boolean, default: false },
    micConsent: { type: Object, default: () => ({}) },
});

/*
 * toRef is used to support optimistic updates. State is mutated
 * on the client, and then hydrated with any updates to the page props
 * as caused by the `router` requests below.
 */
const preferenceValues = toRef(() => props.userProfile);
const userDetails = inject("user_details", ref(null));

// Consent is a UserUIInteractions row rather than a profile field, so it is written through its own
// endpoint rather than the profile POST the other settings use.
const micConsentGranted = ref(props.micConsent.granted);
const micConsentError = ref(false);
const { execute: grantMicConsent, error: grantError } = useFetch(props.endpoints.mic_consent, { immediate: false }).post().json();
const { execute: revokeMicConsent, error: revokeError } = useFetch(props.endpoints.mic_consent, { immediate: false }).delete().json();

// A server round trip can change it too (revoking elsewhere), so track the prop rather than only
// seeding from it once.
watch(
    () => props.micConsent.granted,
    (granted) => (micConsentGranted.value = granted),
);

async function setMicConsent(granted) {
    const previous = micConsentGranted.value;
    micConsentGranted.value = granted;
    micConsentError.value = false;

    const [write, error] = granted ? [grantMicConsent, grantError] : [revokeMicConsent, revokeError];
    await write();

    // useFetch runs with throwOnFailed = false, so a failed write resolves like a success. Roll the
    // toggle back rather than showing consent the server never recorded.
    if (error.value) {
        micConsentGranted.value = previous;
        micConsentError.value = true;
        return;
    }

    // mic_consent_required is resolved server-side and read from shared props by every mic surface,
    // so refresh it here or a revoke leaves the mic unlocked until the next full page load.
    router.reload({ only: ["user_details", "mic_consent"] });
}

// Cookie preference update via API (not Inertia)
const cookiePreferencePayload = ref(null);
const cookiePreferenceError = ref(false);
const cookiePreferenceSaving = ref(false);
// The endpoint answers 204, so no `.json()`: parsing an empty body would register as a failure.
const { execute: updateCookiePreference, error: cookiePreferenceSaveError } = useFetch(props.endpoints.cookie_preference, {
    immediate: false,
}).put(cookiePreferencePayload);

async function setCookiePreference(value) {
    // One save at a time, and none after a success: the page is about to reload with the saved choice.
    if (cookiePreferenceSaving.value) {
        return;
    }
    const previous = preferenceValues.value[FIELDS.COOKIE_CONSENT_CHOICE];
    cookiePreferenceError.value = false;
    cookiePreferenceSaving.value = true;
    preferenceValues.value[FIELDS.COOKIE_CONSENT_CHOICE] = value;
    cookiePreferencePayload.value = { choice: value, method: "profile" };
    await updateCookiePreference();

    // useFetch resolves on a failed write too. Only a recorded save may replace a choice still
    // waiting from before login, otherwise the account's older choice would win on reload.
    if (cookiePreferenceSaveError.value) {
        preferenceValues.value[FIELDS.COOKIE_CONSENT_CHOICE] = previous;
        cookiePreferenceError.value = true;
        cookiePreferenceSaving.value = false;
        return;
    }
    localStorage.setItem("cookie_consent", "persisted");
    rememberBrowserCookieChoice(userDetails.value?.uuid ?? null, value);
    announceCookieConsentChange();
    window.location.reload();
}

function setPreference(field, value) {
    // Cookie preferences use direct API call instead of Inertia
    if (field === FIELDS.COOKIE_CONSENT_CHOICE) {
        setCookiePreference(value);
        return;
    }

    const body = { [field]: value, detail: field };
    preferenceValues.value[field] = value;

    router.post(props.endpoints.profile, body, {
        errorBag: ERROR_BAG,
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
            "X-Inertia-Next": props.endpoints.current_page,
        },
        preserveScroll: true,
        only: ["user_profile", "errors", "user_details"],
    });
}

function idForField(field) {
    return `s_${field}`;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .label-with-info {
        @apply inline-flex items-center gap-1;
    }
}
</style>
